import Microgame from '../engine/Microgame.js';

export default class PingPongGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Ping-Pong !`, en: `Ping-Pong!`, es: `¡Ping-Pong!` },
            controls: { fr: `Glisse ton doigt de bas en haut et inversement pour déplacer la raquette`, en: `Swipe up and down to move your paddle`, es: `Desliza arriba y abajo para mover la raqueta` },
            objective: { fr: `Renvoie la balle sans la rater`, en: `Hit the ball back without missing`, es: `Devuelve la pelota sin fallar` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="m14 10-6 6M2 20h8v-3l7-7a2 2 0 1 0-3-3L7 14v3"/><circle cx="18" cy="18" r="2"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 90, duration: 420 },
                { type: 'wait', duration: 1400 },
                { type: 'wait', duration: 480 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.paddle = null;
        this.ball = null;
        this.ballX = 50; // %
        this.ballY = 10; // %

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.35;

        // Randomize start direction, scale with difficulty
        const baseVX = this.isPlayback ? 0 : ((Math.random() * 2 - 1) * 1.2); // range -1.2 to 1.2, includes center
        const baseVY = this.isPlayback ? 1.3 : (1.2 + Math.random() * 0.3);
        this.ballVX = baseVX * adj;
        this.ballVY = baseVY * adj;
        this.paddleX = 50; // %
        this.prevPaddleX = 50;

        // Duration scales with speed
        this.duration = 3200 * (speedMult / adj);

        // Paddle width shrinks slightly at higher speeds
        this.paddleWidth = Math.max(55, 80 - (adj - 1) * 12);

        this._isAnimating = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "RETURN!";

        // Table surface gradient
        const surface = document.createElement('div');
        surface.style.position = 'absolute';
        surface.style.width = '100%';
        surface.style.height = '100%';
        surface.style.background = `linear-gradient(180deg, ${this.darken(this.secondaryColor, 0.08)} 0%, ${this.darken(this.secondaryColor, 0.15)} 100%)`;
        this.container.appendChild(surface);

        // Net
        const net = document.createElement('div');
        net.style.position = 'absolute';
        net.style.width = '100%';
        net.style.height = '3px';
        net.style.top = '50%';
        net.style.opacity = '0.4';
        this.container.appendChild(net);

        // Dotted net effect
        for (let i = 0; i < 15; i++) {
            const dot = document.createElement('div');
            dot.style.position = 'absolute';
            dot.style.width = '8px';
            dot.style.height = '3px';
            dot.style.background = this.secondaryColor;
            dot.style.left = `${i * 7}%`;
            dot.style.top = '0';
            dot.style.borderRadius = '1px';
            net.appendChild(dot);
        }

        // Ball
        this.ball = document.createElement('div');
        this.ball.style.position = 'absolute';
        this.ball.style.width = '20px';
        this.ball.style.height = '20px';
        this.ball.style.background = this.secondaryColor;
        this.ball.style.borderRadius = '50%';
        this.ball.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
        this.ball.style.zIndex = '3';
        this.container.appendChild(this.ball);

        // Ball trail container
        this.trailContainer = document.createElement('div');
        this.trailContainer.style.position = 'absolute';
        this.trailContainer.style.width = '100%';
        this.trailContainer.style.height = '100%';
        this.trailContainer.style.top = '0';
        this.trailContainer.style.left = '0';
        this.trailContainer.style.pointerEvents = 'none';
        this.trailContainer.style.zIndex = '2';
        this.container.appendChild(this.trailContainer);

        // Paddle
        this.paddle = document.createElement('div');
        this.paddle.style.position = 'absolute';
        this.paddle.style.width = `${this.paddleWidth}px`;
        this.paddle.style.height = '16px';
        this.paddle.style.background = this.primaryColor;
        this.paddle.style.bottom = '10%';
        this.paddle.style.left = '50%';
        this.paddle.style.transform = 'translateX(-50%)';
        this.paddle.style.borderRadius = '8px';
        this.paddle.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.paddle.style.zIndex = '4';
        this.container.appendChild(this.paddle);

        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);

        this.trailTimer = 0;
        this.animate();
    }

    darken(hex, factor) {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = hex;
        const match = ctx.fillStyle.match(/\d+/g);
        if (!match) return hex;
        return `rgb(${Math.floor(match[0] * factor)},${Math.floor(match[1] * factor)},${Math.floor(match[2] * factor)})`;
    }

    animate() {
        if (!this.active) return;

        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }

        // Move Ball
        this.ballX += this.ballVX;
        this.ballY += this.ballVY;

        // Bounce Walls
        if (this.ballX <= 2 || this.ballX >= 95) this.ballVX *= -1;
        // Bounce top
        if (this.ballY <= 2) this.ballVY = Math.abs(this.ballVY);

        // Trail effect
        this.trailTimer++;
        if (this.trailTimer % 3 === 0) {
            const trail = document.createElement('div');
            trail.style.position = 'absolute';
            trail.style.width = '12px';
            trail.style.height = '12px';
            trail.style.background = this.secondaryColor;
            trail.style.borderRadius = '50%';
            trail.style.left = `${this.ballX}%`;
            trail.style.top = `${this.ballY}%`;
            trail.style.opacity = '0.3';
            trail.style.pointerEvents = 'none';
            this.trailContainer.appendChild(trail);

            trail.animate([
                { opacity: 0.3, transform: 'scale(1)' },
                { opacity: 0, transform: 'scale(0.3)' }
            ], { duration: 200 });
            setTimeout(() => trail.remove(), 200);
        }

        // Update Visuals
        this.ball.style.left = `${this.ballX}%`;
        this.ball.style.top = `${this.ballY}%`;

        // Ball shadow stretching
        const speed = Math.sqrt(this.ballVX ** 2 + this.ballVY ** 2);
        const scaleX = 1 + Math.abs(this.ballVX) * 0.05;
        const scaleY = 1 + Math.abs(this.ballVY) * 0.03;
        this.ball.style.transform = `translate(-50%, -50%) scale(${scaleX}, ${scaleY})`;

        // Hit zone: paddle is at ~90% Y. Expand Y zone to catch fast-falling balls.
        const paddleHalfW = (this.paddleWidth / (this.container.clientWidth || 400)) * 50;

        if (this.ballVY > 0 && this.ballY >= 86 && this.ballY <= 98) {
            // Swept AABB for the paddle to fix swipe tunneling
            const paddleLeft = Math.min(this.prevPaddleX, this.paddleX) - paddleHalfW - 3;
            const paddleRight = Math.max(this.prevPaddleX, this.paddleX) + paddleHalfW + 3;

            if (this.ballX > paddleLeft && this.ballX < paddleRight) {
                // Angle based on where ball hits paddle
                const hitPos = (this.ballX - this.paddleX) / paddleHalfW; // -1 to 1
                this.ballVX = hitPos * 2;
                this.ballVY *= -1.3; // Bounce back faster
                this.ballY = 87; // Prevent double hit
                this.createHitEffect();

                // Freeze and win after seeing the ball fly back
                this._isAnimating = true;
                this._frozenTimerValue = this.engine.timer;
                if (!this.won) {
                    setTimeout(() => this.win(), 400);
                }
            }
        }

        // Miss
        if (this.ballY > 102) {
            this.lose();
        }

        this.prevPaddleX = this.paddleX;
        requestAnimationFrame(() => this.animate());
    }

    createHitEffect() {
        // Impact ripple
        const hit = document.createElement('div');
        hit.style.position = 'absolute';
        hit.style.width = '40px';
        hit.style.height = '40px';
        hit.style.border = `3px solid ${this.primaryColor}`;
        hit.style.borderRadius = '50%';
        hit.style.left = `${this.ballX}%`;
        hit.style.top = `${this.ballY}%`;
        hit.style.transform = 'translate(-50%, -50%) scale(0)';
        hit.style.zIndex = '5';
        this.container.appendChild(hit);

        hit.animate([
            { transform: 'translate(-50%, -50%) scale(0)', opacity: 1 },
            { transform: 'translate(-50%, -50%) scale(2.5)', opacity: 0 }
        ], { duration: 350 });
        setTimeout(() => hit.remove(), 350);

        // Paddle flash
        this.paddle.animate([
            { boxShadow: `0 0 30px ${this.primaryColor}` },
            { boxShadow: `0 0 12px ${this.primaryColor}66` }
        ], { duration: 200 });
    }

    update(dt) {
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }
    }

    onMove(e) {
        if (this.won || this.lost) return;

        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        this.paddleX = (x / rect.width) * 100;
        this.paddleX = Math.max(10, Math.min(90, this.paddleX));

        this.paddle.style.left = `${this.paddleX}%`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
    }
}
